import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import TabFormWrapper from "../../Shared/TabFormWrapper";
import FormFooter from "../../Shared/FormFooter";
import AlertRed from "../../Shared/AlertRed";
import AlertGreen from "../../Authentication/AlertGreen";
import BookingPageFormSkeleton from "../../Shared/LoadingSkeletons/BookingPageFormSkeleton";
import TravelFeeFields from "./TravelFeeFields";
import {
  useBookingPage,
  useUpdateSettingsTab,
} from "../../../hooks/useBookingPage";
import { deriveDistanceUnit } from "../../../utility/distanceUnitUtils";

const TravelFeeForm = () => {
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);

  // Fetch config data
  const { data: configData, isLoading } = useBookingPage();

  // Update mutation for settings tab
  const { mutate: updateSettings, isPending: isUpdating } =
    useUpdateSettingsTab();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      travelFeesEnabled: false,
      travelFeeType: "flat",
      travelFeeFlatRate: 0,
      travelFeePerDistance: 0,
      travelFeeDistanceUnit: "km",
      baseLocation: null,
    },
  });

  // Set form values when data loads
  useEffect(() => {
    if (configData?.data) {
      const formData = {
        travelFeesEnabled: configData.data.travelFeesEnabled || false,
        travelFeeType: configData.data.travelFeeType || "flat",
        travelFeeFlatRate: configData.data.travelFeeFlatRate
          ? parseFloat(configData.data.travelFeeFlatRate)
          : 0,
        travelFeePerDistance: configData.data.travelFeePerDistance
          ? parseFloat(configData.data.travelFeePerDistance)
          : 0,
        travelFeeDistanceUnit: configData.data.travelFeeDistanceUnit || "km",
        baseLocation: configData.data.baseLocation || null,
      };

      // Reset form and mark as pristine (not dirty)
      reset(formData, { keepDirty: false });

      // Set selected address if base location exists
      if (configData.data.baseLocation) {
        setSelectedAddress({
          id: configData.data.baseLocation.placeId,
          name: configData.data.baseLocation.address,
        });
      } else {
        setSelectedAddress(null);
      }
    }
  }, [configData, reset]);

  const handleAddressSelect = (address) => {
    setSelectedAddress(address);
    // Update form value with the selected address
    if (address && address.id && address.name) {
      setValue(
        "baseLocation",
        {
          placeId: address.id,
          address: address.name,
        },
        { shouldDirty: true }
      );
      setValue(
        "travelFeeDistanceUnit",
        deriveDistanceUnit(address.name),
        { shouldDirty: true }
      );
    } else {
      // Clear the base location when address is null or invalid (edit mode)
      setValue("baseLocation", null, { shouldDirty: true });
    }
    setErrors({});
    setSuccess(false);
  };

  const onSubmit = async (data) => {
    // Format travel fee values to ensure proper Decimal128 format
    const formattedData = {
      ...data,
      travelFeeFlatRate: data.travelFeeFlatRate
        ? parseFloat(data.travelFeeFlatRate).toFixed(2)
        : "0.00",
      travelFeePerDistance: data.travelFeePerDistance
        ? parseFloat(data.travelFeePerDistance).toFixed(2)
        : "0.00",
    };
    await performSave(formattedData);
  };

  const performSave = async (data) => {
    setErrors({});
    setSuccess(false);

    updateSettings(data, {
      onSuccess: () => {
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
        }, 3000);
      },
      onError: (err) => {
        const errorMessage = err.response?.data?.error?.message;
        const errorDetails = err.response?.data?.error?.details;

        let displayMessage =
          errorMessage || "Failed to update travel fee settings";

        // Handle specific validation errors
        if (errorDetails && Array.isArray(errorDetails)) {
          displayMessage = errorDetails.join(", ");
        }

        setErrors({
          title: "Error",
          message: displayMessage,
        });
      },
    });
  };

  if (isLoading) {
    return (
      <TabFormWrapper>
        <BookingPageFormSkeleton showToggle={true} inputCount={3} />
      </TabFormWrapper>
    );
  }

  return (
    <TabFormWrapper onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-6">
        <div className="space-y-4">
          <TravelFeeFields
            register={register}
            watch={watch}
            setValue={setValue}
            selectedAddress={selectedAddress}
            onAddressSelect={handleAddressSelect}
          />
        </div>
      </div>
      <FormFooter
        primaryCaption="Save"
        isLoading={isUpdating}
        disabled={!isDirty}
        requiresVerification={false}
      />

      {/* Alerts */}
      {errors.title && (
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <AlertRed title={errors.title} message={errors.message} />
        </div>
      )}

      {success && (
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <AlertGreen
            title="Success"
            message="Travel fee settings updated successfully"
          />
        </div>
      )}
    </TabFormWrapper>
  );
};

export default TravelFeeForm;
